import _extends from "@babel/runtime/helpers/extends";
import React, { useContext } from 'react';
import { bool, func, string } from 'prop-types';
import { useFela } from 'react-fela';
import { useId } from '../../utils/auto-id';
import { deprecate } from '../../utils/deprecate';
import { Block } from '../block';
import { RadioContext } from '../radioGroup';
import { View } from '../view';

const radioStyle = _ref => {
  let {
    theme,
    isValid
  } = _ref;
  return {
    boxSizing: 'border-box',
    borderWidth: 1,
    borderStyle: 'solid',
    appearance: 'none',
    borderRadius: '50%',
    flexShrink: 0,
    width: 20,
    height: 20,
    margin: 0,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    cursor: 'pointer',
    borderColor: theme.tokens.inputControlBorder,
    backgroundColor: theme.tokens.inputBackground,
    outline: 0,
    extend: [{
      condition: !isValid,
      style: {
        borderColor: theme.color.foreground.alert,
        borderWidth: 2
      }
    }, {
      condition: isValid,
      style: {
        ':focus': {
          borderColor: theme.tokens.inputBorderFocus
        }
      }
    }],
    ':checked': {
      backgroundColor: theme.tokens.inputControlBackground,
      borderColor: theme.tokens.inputControlBackground,
      ':before': {
        borderRadius: '50%',
        display: 'block',
        content: "''",
        width: 8,
        height: 8,
        position: 'absolute',
        backgroundColor: theme.tokens.inputControlForeground
      }
    },
    ':disabled': {
      cursor: 'not-allowed',
      borderColor: theme.tokens.inputDisabledBorder,
      backgroundColor: theme.tokens.inputDisabledBackground,
      ':checked': {
        borderColor: theme.tokens.inputDisabledBorder,
        backgroundColor: theme.tokens.inputDisabledBackground,
        ':before': {
          backgroundColor: theme.tokens.inputDisabledControl
        }
      }
    }
  };
};

const labelStyle = _ref2 => {
  let {
    theme,
    isValid
  } = _ref2;
  return {
    color: isValid ? theme.color.foreground.secondary : theme.color.foreground.alert,
    fontSize: 16,
    letterSpacing: '0.02em',
    marginTop: -1,
    fontFamily: theme.fontTypes.NOVUM,
    fontWeight: 300
  };
};

export const Radio = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    isValid: userIsValid,
    value: userValue,
    onChange: userOnChange,
    checked: userChecked,
    name: userName,
    label = '',
    ...props
  } = _ref3;
  const {
    theme
  } = useFela();
  const {
    name,
    value,
    isValid,
    onChange
  } = useContext(RadioContext);
  const styleProps = {
    isValid: userIsValid !== undefined ? userIsValid : isValid !== undefined ? isValid : true,
    theme
  };
  const id = useId(name ? name + '-' + userValue : userValue, props.id);
  const checked = userChecked || value === userValue; // legacy check

  const hasLabel = label.length > 0;
  deprecate('Unlabelled radio-input fields are deprecated and will be removed in 3.0.0. Pass a label prop.', !hasLabel);
  const hasValue = value && value.length > 0;
  deprecate('Radio buttons with no value provided are deprecated and will be removed in 3.0.0. Pass a value prop.', !hasValue);
  return /*#__PURE__*/React.createElement(View, {
    spacing: 1,
    direction: "row",
    alignItems: "center",
    shrink: 1
  }, /*#__PURE__*/React.createElement(Block, _extends({
    ref: ref,
    onChange: event => {
      if (userOnChange) {
        userOnChange(event);
      } else if (onChange) {
        onChange(event);
      }
    },
    checked: checked
  }, props, {
    as: "input",
    type: "radio",
    value: userValue,
    name: name || userName,
    id: id,
    extend: radioStyle(styleProps)
  })), !hasLabel ? null : /*#__PURE__*/React.createElement(Block, {
    as: "label",
    htmlFor: id,
    style: labelStyle(styleProps)
  }, label));
});
Radio.displayName = 'Radio';
Radio.propTypes = {
  onChange: func,
  checked: bool,

  /** Should be the same for all radio buttons belonging to same group. */
  name: string,
  id: string,
  isValid: bool,

  /** Value of the radio button. */
  value: string,

  /** The label text that is associated with the radio button. */
  label: string
};